import { collectionPlaceField, PLACE_REQUIRED_HEADERS } from '@/features/collection-place/collection-place.contract';
/**
 * Theme, Sector and Contributions API Module
 * All API calls for themes, sectors, phrases, and contributions management
 */

import { apiClient } from "./api-client";
import { getSectorQuestionPage, toQuestion } from './sector-questions.api';
import type {
  Theme,
  CreateThemeDto,
  UpdateThemeDto,
  Sector,
  CreateSectorDto,
  UpdateSectorDto,
  Phrase,
  Contribution,
  CreateContributionDto,
  CreateAudioContributionWithFilesDto,
  ValidateContributionDto,
  CreateThemeWithExcelDto,
  ExcelQuestion,
  PaginatedThemesResponse,
  PaginatedSectorsResponse,
  PaginatedPhrasesResponse,
  PaginatedContributionsResponse,
  ContributionValidationResponse,
} from '@/types/theme.types';

// ============================================
// THEMES API
// ============================================

export const themeApi = {
  // Get all themes (public)
  getThemes: async (page: number = 1, limit: number = 20) => {
    const { data } = await apiClient.get<PaginatedThemesResponse>('/themes', {
      params: { page, limit },
    });
    return data;
  },

  // Get my themes (admin only)
  getMyThemes: async (page: number = 1, limit: number = 20) => {
    const { data } = await apiClient.get<PaginatedThemesResponse>('/themes/admin/mine', {
      params: { page, limit },
    });
    return data;
  },

  // Get theme by ID
  getThemeById: async (id: string) => {
    const { data } = await apiClient.get<Theme>(`/themes/${id}`);
    return data;
  },

  // Create a new theme (admin only)
  createTheme: async (payload: CreateThemeDto) => {
    const { data } = await apiClient.post<Theme>('/themes', payload);
    return data;
  },

  // Update theme (admin only - owner or super_admin)
  updateTheme: async (id: string, payload: UpdateThemeDto) => {
    const { data } = await apiClient.put<Theme>(`/themes/${id}`, payload);
    return data;
  },

  // Delete theme (admin only - owner or super_admin)
  deleteTheme: async (id: string) => {
    const { data } = await apiClient.delete(`/themes/${id}`);
    return data;
  },

  // ============================================
  // SECTORS API
  // ============================================

  // Get all sectors (admin only)
  getSectors: async (page: number = 1, limit: number = 50) => {
    const { data } = await apiClient.get<PaginatedSectorsResponse | Sector[]>('/themes/sectors', {
      params: { page, limit },
    });

    if (Array.isArray(data)) {
      return {
        data,
        total: data.length,
        page,
        limit,
        hasMore: false,
      } as PaginatedSectorsResponse;
    }

    if (data && Array.isArray((data as PaginatedSectorsResponse).data)) {
      return data as PaginatedSectorsResponse;
    }

    return {
      data: [],
      total: 0,
      page,
      limit,
      hasMore: false,
    } as PaginatedSectorsResponse;
  },

  // Get sector by ID
  getSectorById: async (sectorId: string) => {
    const { data } = await apiClient.get<Sector>(`/themes/sectors/${sectorId}`);
    return data;
  },

  // Create a new sector (admin only)
  createSector: async (payload: CreateSectorDto) => {
    const { data } = await apiClient.post<Sector>('/themes/sectors', payload);
    return data;
  },

  // Update sector (admin only)
  updateSector: async (sectorId: string, payload: UpdateSectorDto) => {
    const { data } = await apiClient.put<Sector>(`/themes/sectors/${sectorId}`, payload);
    return data;
  },

  // Delete sector (admin only)
  deleteSector: async (sectorId: string) => {
    const { data } = await apiClient.delete(`/themes/sectors/${sectorId}`);
    return data;
  },

  // ============================================
  // PHRASES/QUESTIONS API
  // ============================================

  // Get phrases by theme and language
  getPhrasesByTheme: async (
    themeId: string,
    language?: string,
    page: number = 1,
    limit: number = 100
  ) => {
    const { data } = await apiClient.get<PaginatedPhrasesResponse>(
      `/themes/${themeId}/phrases`,
      {
        params: { language, page, limit },
      }
    );
    return data;
  },

  // Get questions by sector
  getQuestionsBySector: async (
    sectorId: string,
    language?: string,
    page: number = 1,
    limit: number = 100
  ) => {
    const response = await getSectorQuestionPage(sectorId, language, page, limit);
    return {
      data: response.data.map(toQuestion),
      total: response.meta.pagination.total,
      page: response.meta.pagination.page,
      limit: response.meta.pagination.limit,
      hasMore: response.meta.pagination.has_more,
    };
  },

  // ============================================
  // THEMES WITH EXCEL API
  // ============================================

  // Create theme with Excel file (admin only)
  createThemeWithExcel: async (payload: {
    title?: string;
    description?: string;
    sector: string;
    language?: string;
    excelFile: File | Blob;
  }) => {
    const formData = new FormData();
    if (payload.title) formData.append('title', payload.title);
    if (payload.description) formData.append('description', payload.description);
    formData.append('sector', payload.sector);
    if (payload.language) formData.append('language', payload.language);
    formData.append('excelFile', payload.excelFile);

    const { data } = await apiClient.post<Theme>('/themes/excel', formData);
    return data;
  },

  // ============================================
  // CONTRIBUTIONS API
  // ============================================

  // Create a contribution (user audio submission)
  // A collection session sets isPlaceRequired: the server then refuses an answer without its place.
  createContribution: async (payload: CreateContributionDto, isPlaceRequired = false) => {
    const { data } = await apiClient.post<Contribution>('/themes/contributions', payload, {
      headers: isPlaceRequired ? PLACE_REQUIRED_HEADERS : {},
    });
    return data;
  },

  // Create a contribution with uploaded audio file
  createContributionWithAudioFiles: async (payload: CreateAudioContributionWithFilesDto) => {
    const formData = new FormData();
    formData.append('sectorId', payload.sectorId);
    if (payload.questionId) {
      formData.append('questionId', payload.questionId);
    }
    if (payload.language) formData.append('language', payload.language);
    if (payload.questionTextLocal) formData.append('questionTextLocal', payload.questionTextLocal);
    if (payload.responseTextLocal) formData.append('responseTextLocal', payload.responseTextLocal);
    formData.append('recordingDurationMs', String(payload.recordingDurationMs));
    if (payload.recordingDeviceType) formData.append('recordingDeviceType', payload.recordingDeviceType);
    if (payload.recordingDeviceCategory) formData.append('recordingDeviceCategory', payload.recordingDeviceCategory);
    if (payload.recordingMicrophoneType) formData.append('recordingMicrophoneType', payload.recordingMicrophoneType);
    if (payload.recordingDeviceDetails) formData.append('recordingDeviceDetails', payload.recordingDeviceDetails);
    if (payload.communityProfileId) formData.append('communityProfileId', payload.communityProfileId);
    if (payload.communityCode) formData.append('communityCode', payload.communityCode);
    if (payload.collectionPlace) formData.append('collectionPlace', collectionPlaceField(payload.collectionPlace));
    if (payload.audioQuality) formData.append('audioQuality', JSON.stringify(payload.audioQuality));

    if (Array.isArray(payload.userIds)) {
      payload.userIds.forEach((id) => {
        formData.append('userIds', id);
      });
    }

    formData.append('audio', payload.responseAudioFile);

    const { data } = await apiClient.post<Contribution>(
      '/themes/contributions/audio',
      formData,
      { headers: PLACE_REQUIRED_HEADERS },
    );
    return data;
  },

  // Get my contributions for a specific sector
  getContributionsBySector: async (
    sectorId: string,
    page: number = 1,
    limit: number = 50
  ) => {
    const { data } = await apiClient.get<PaginatedContributionsResponse>(
      `/themes/contributions/${sectorId}`,
      {
        params: { page, limit },
      }
    );
    return data;
  },

  // Get all my contributions
  getUserContributions: async (page: number = 1, limit: number = 50) => {
    const { data } = await apiClient.get<PaginatedContributionsResponse>(
      '/themes/contributions/user/all',
      {
        params: { page, limit },
      }
    );
    return data;
  },

  // Get all contributions for validation (admin only)
  getContributionsForValidation: async (
    status?: string,
    sectorId?: string,
    page: number = 1,
    limit: number = 50
  ) => {
    const { data } = await apiClient.get<PaginatedContributionsResponse>(
      '/themes/admin/contributions',
      {
        params: { status, sectorId, page, limit },
      }
    );
    return data;
  },

  // Validate/approve a contribution (admin only)
  validateContribution: async (
    contributionId: string,
    payload: ValidateContributionDto
  ) => {
    const { data } = await apiClient.put<ContributionValidationResponse>(
      `/themes/contributions/${contributionId}/validate`,
      payload
    );
    return data;
  },
};
